import type { ReactNode } from 'react';
import { T } from 'gt-react';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { PlusIcon, XIcon, LockIcon } from '@phosphor-icons/react';
import { CoreEnumDTO } from '@/api/core/core-enums/useCoreEnums';
import { useCoreEnumModal, type EnumOption } from './useCoreEnumModal';

type ModalState = ReturnType<typeof useCoreEnumModal>;

export function CoreEnumModalForm({
  modal,
  enumToEdit,
  createDescription,
  optionNamePlaceholder,
  onClose,
}: {
  modal: ModalState;
  enumToEdit?: CoreEnumDTO | null;
  createDescription?: ReactNode;
  optionNamePlaceholder?: string;
  onClose: () => void;
}) {
  const identityLocked = modal.isEditMode && Boolean(enumToEdit?.isSystem);

  return (
    <>
      <CoreEnumModalHeader
        isEditMode={modal.isEditMode}
        isSystem={Boolean(enumToEdit?.isSystem)}
        createDescription={createDescription}
      />
      <div className="space-y-4">
        {identityLocked && <CoreEnumSystemNotice />}
        {modal.isEditMode && modal.areOptionsLocked && !modal.isAdmin && (
          <CoreEnumLockedNotice />
        )}
        <CoreEnumIdentityFields modal={modal} identityLocked={identityLocked} />
        {modal.isAdmin && <CoreEnumAdminToggles modal={modal} />}
        <CoreEnumOptionsEditor
          options={modal.options}
          areOptionsLocked={modal.areOptionsLocked}
          isAdmin={modal.isAdmin}
          optionNamePlaceholder={optionNamePlaceholder}
          gt={modal.gt}
          onAdd={modal.handleAddOption}
          onRemove={modal.handleRemoveOption}
          onChange={modal.handleOptionChange}
        />
      </div>
      <CoreEnumModalActions
        isEditMode={modal.isEditMode}
        isSubmitting={modal.isSubmitting}
        canSubmit={modal.canSubmit}
        onClose={onClose}
        onSubmit={modal.handleSubmit}
      />
    </>
  );
}

function CoreEnumModalHeader({
  isEditMode,
  isSystem,
  createDescription,
}: {
  isEditMode: boolean;
  isSystem: boolean;
  createDescription?: ReactNode;
}) {
  return (
    <DialogHeader>
      <div className="flex items-center gap-2">
        <DialogTitle>
          {isEditMode ? (
            <T context="dialog title when editing an existing enum">
              Edit Enum
            </T>
          ) : (
            <T context="dialog title when creating a new enum">Create Enum</T>
          )}
        </DialogTitle>
        {isEditMode && isSystem && (
          <div className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-sm font-medium text-blue-700">
            <LockIcon className="h-3.5 w-3.5" />
            <T context="badge label indicating this is a system-managed enum">
              System
            </T>
          </div>
        )}
      </div>
      <DialogDescription>
        <CoreEnumModalDescription
          isEditMode={isEditMode}
          isSystem={isSystem}
          createDescription={createDescription}
        />
      </DialogDescription>
    </DialogHeader>
  );
}

function CoreEnumModalDescription({
  isEditMode,
  isSystem,
  createDescription,
}: {
  isEditMode: boolean;
  isSystem: boolean;
  createDescription?: ReactNode;
}) {
  if (isEditMode && isSystem) {
    return (
      <T context="dialog description for a system enum that only allows editing options">
        This is a system-managed enum. You can only edit the options below.
      </T>
    );
  }
  if (isEditMode) {
    return (
      <T context="dialog description when editing an existing enum">
        Update the enum and its options
      </T>
    );
  }
  return (
    createDescription ?? (
      <T context="dialog description when creating a new enum">
        Create a reusable list of named values. Elsewhere in the product you
        pick this enum, and its option names become the allowed choices.
      </T>
    )
  );
}

function CoreEnumSystemNotice() {
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
      <p className="flex items-center gap-2 text-sm text-blue-800">
        <LockIcon className="h-4 w-4" />
        <strong>
          <T context="label in info box indicating this is a system enum">
            System Enum:
          </T>
        </strong>
      </p>
      <p className="mt-1 text-sm text-blue-800">
        <T context="info message explaining that name and description are locked for system enums">
          Name and description are locked. You can only manage the options
          below.
        </T>
      </p>
    </div>
  );
}

function CoreEnumLockedNotice() {
  return (
    <div className="rounded-md border border-purple-200 bg-purple-50 p-3">
      <p className="flex items-center gap-2 text-sm text-purple-800">
        <LockIcon className="h-4 w-4" />
        <strong>
          <T context="label in info box indicating the enum options are locked">
            Locked Options:
          </T>
        </strong>
      </p>
      <p className="mt-1 text-sm text-purple-800">
        <T context="info message explaining that enum options are locked and only admins can change them">
          The options for this enum are locked. Contact an administrator for
          changes.
        </T>
      </p>
    </div>
  );
}

function CoreEnumIdentityFields({
  modal,
  identityLocked,
}: {
  modal: ModalState;
  identityLocked: boolean;
}) {
  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="core-enum-name">
            <T context="label for the enum name input field">Name</T>
          </Label>
          {identityLocked && <LockIcon className="h-3.5 w-3.5 text-gray-400" />}
        </div>
        <Input
          id="core-enum-name"
          value={modal.name}
          onChange={(e) => modal.setName(e.target.value)}
          placeholder={modal.gt('e.g., Invoice approval outcomes', {
            context:
              'placeholder for enum name input, showing an example enum name',
          })}
          disabled={identityLocked}
          className={identityLocked ? 'bg-gray-100' : ''}
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="core-enum-description">
            <T context="label for the enum description textarea">Description</T>
          </Label>
          {identityLocked && <LockIcon className="h-3.5 w-3.5 text-gray-400" />}
        </div>
        <Textarea
          id="core-enum-description"
          value={modal.description}
          onChange={(e) => modal.setDescription(e.target.value)}
          placeholder={modal.gt('Describe what this list of values is for', {
            context: 'placeholder for enum description textarea',
          })}
          rows={3}
          disabled={identityLocked}
          className={identityLocked ? 'bg-gray-100' : ''}
        />
      </div>
    </>
  );
}

function CoreEnumAdminToggles({ modal }: { modal: ModalState }) {
  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="isSystem">
            <T context="label for system enum toggle switch">System Enum</T>
          </Label>
          <p className="text-xs text-gray-600">
            <T context="description explaining that system enums have protected names and descriptions">
              System enums have protected names and descriptions
            </T>
          </p>
        </div>
        <Switch
          id="isSystem"
          checked={modal.isSystem}
          onCheckedChange={modal.setIsSystem}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="areOptionsLocked">
            <T context="label for lock options toggle switch">Lock Options</T>
          </Label>
          <p className="text-xs text-gray-600">
            <T context="description explaining that locking options prevents non-admin users from modifying them">
              Prevent non-admin users from modifying enum options
            </T>
          </p>
        </div>
        <Switch
          id="areOptionsLocked"
          checked={modal.areOptionsLocked}
          onCheckedChange={modal.setAreOptionsLocked}
        />
      </div>
    </div>
  );
}

function CoreEnumModalActions({
  isEditMode,
  isSubmitting,
  canSubmit,
  onClose,
  onSubmit,
}: {
  isEditMode: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <DialogFooter>
      <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
        <T context="button to cancel and close the enum editor dialog">
          Cancel
        </T>
      </Button>
      <Button onClick={onSubmit} disabled={!canSubmit}>
        {isSubmitting ? (
          <T context="button label while the enum is being saved">Saving...</T>
        ) : isEditMode ? (
          <T context="button to submit and update an existing enum">Update</T>
        ) : (
          <T context="button to submit and create a new enum">Create</T>
        )}
      </Button>
    </DialogFooter>
  );
}

function CoreEnumOptionsEditor({
  options,
  areOptionsLocked,
  isAdmin,
  optionNamePlaceholder,
  gt,
  onAdd,
  onRemove,
  onChange,
}: {
  options: EnumOption[];
  areOptionsLocked: boolean;
  isAdmin: boolean;
  optionNamePlaceholder?: string;
  gt: ModalState['gt'];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (
    index: number,
    field: keyof EnumOption,
    value: string | number,
  ) => void;
}) {
  const optionsLocked = areOptionsLocked && !isAdmin;

  return (
    <div className="space-y-2 border-t pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">
            <T context="heading for the options section of the enum editor">
              Options
            </T>
          </h3>
          <p className="text-xs text-gray-600">
            <T context="description below the Options heading explaining users can add and manage enum options">
              Each option name is a selectable value. Empty rows are ignored on
              save.
            </T>
          </p>
        </div>
        {!optionsLocked && (
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <PlusIcon className="mr-1 h-4 w-4" />
            <T context="button to add a new option to the enum">Add Option</T>
          </Button>
        )}
      </div>

      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="empty state message when no options have been added to the enum yet">
            No options yet. Add at least one named option.
          </T>
        </p>
      ) : (
        <div className="space-y-3">
          {options.map((option, index) => (
            <CoreEnumOptionRow
              key={option.id ?? `new-${index}`}
              option={option}
              index={index}
              optionsLocked={optionsLocked}
              optionNamePlaceholder={optionNamePlaceholder}
              gt={gt}
              onRemove={onRemove}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CoreEnumOptionRow({
  option,
  index,
  optionsLocked,
  optionNamePlaceholder,
  gt,
  onRemove,
  onChange,
}: {
  option: EnumOption;
  index: number;
  optionsLocked: boolean;
  optionNamePlaceholder?: string;
  gt: ModalState['gt'];
  onRemove: (index: number) => void;
  onChange: (
    index: number,
    field: keyof EnumOption,
    value: string | number,
  ) => void;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border p-3">
      <div className="flex-1 space-y-2">
        <Input
          value={option.name}
          onChange={(e) => onChange(index, 'name', e.target.value)}
          placeholder={
            optionNamePlaceholder ??
            gt('Option name (e.g. approved)', {
              context: 'placeholder for the enum option name input field',
            })
          }
          aria-label={gt('Option {index} name', {
            context: 'accessible name for the enum option name input field',
            index: String(index + 1),
          })}
          disabled={optionsLocked}
          className={optionsLocked ? 'bg-gray-100' : ''}
        />
        <Input
          value={option.description}
          onChange={(e) => onChange(index, 'description', e.target.value)}
          placeholder={gt('Option description (optional)', {
            context: 'placeholder for the enum option description input field',
          })}
          aria-label={gt('Option {index} description', {
            context:
              'accessible name for the enum option description input field',
            index: String(index + 1),
          })}
          disabled={optionsLocked}
          className={optionsLocked ? 'bg-gray-100' : ''}
        />
      </div>
      {!optionsLocked && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          aria-label={gt('Remove option {index}', {
            context:
              'accessible name for the button that removes an enum option',
            index: String(index + 1),
          })}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
